/**
 * Apply a template's styles with Word's own style import.
 *
 * The template is opened as a hidden, unsaved document (WordApi 1.5) only to
 * read its style sheet; the styles go into the open document through
 * `importStylesFromJson` with `Overwrite` (WordApi 1.6), so Word itself
 * rewrites Normal, the headings, lists and tables, borders and shading
 * included, and every paragraph using them changes at once. Hosts without
 * these sets report `unsupported` and the agent falls back to reading the
 * template on the server and formatting style by style.
 */
import { isOfficeDocumentAvailable } from "./snapshot";

export type TemplateStylesResult =
  | {
      status: "applied";
      imported: string[];
      count: number;
      /** Every paragraph/character style the template defines. */ names: string[];
    }
  | { status: "unsupported"; reason: string }
  | { status: "error"; message: string };

const supports = (set: string): boolean => {
  try {
    return Office.context.requirements.isSetSupported("WordApi", set);
  } catch {
    return false;
  }
};

/** Whether this host can import a template's styles natively. */
export const canImportTemplateStyles = (): boolean => isOfficeDocumentAvailable() && supports("1.6");

const STYLE_SCALARS = [
  "nameLocal",
  "type",
  "builtIn",
  "baseStyle",
  "nextParagraphStyle",
  "priority",
  "quickStyle",
  "linked",
  "inUse",
  "unhideWhenUsed",
  "visibility",
] as const;

type StyleEntry = Record<string, unknown> & { nameLocal: string };

const STYLE_LOADS = `items/font,items/paragraphFormat,items/shading,${STYLE_SCALARS.map((p) => `items/${p}`).join(",")}`;

const entryOf = (style: Word.Style): StyleEntry => {
  const data = style.toJSON() as Record<string, unknown>;
  const entry: Record<string, unknown> = {};
  for (const key of STYLE_SCALARS) if (data[key] !== undefined) entry[key] = data[key];
  entry.font = data.font ?? null;
  entry.paragraphFormat = String(style.type) === "Paragraph" ? (data.paragraphFormat ?? null) : null;
  entry.shading = data.shading ?? null;
  return entry as StyleEntry;
};

const paragraphOrCharacter = (style: Word.Style): boolean => {
  const type = String(style.type);
  // Table and list styles need parts the export does not carry reliably.
  return type === "Paragraph" || type === "Character";
};

/**
 * The paragraph and character styles a .docx defines, as Word's style JSON.
 * The template is opened as a hidden document only to read them.
 * @param base64 - The template file.
 */
export const readTemplateStyleEntries = async (base64: string): Promise<StyleEntry[]> =>
  Word.run(async (context) => {
    const template = context.application.createDocument(base64);
    const styles = template.getStyles();
    styles.load(STYLE_LOADS);
    await context.sync();
    return styles.items.filter(paragraphOrCharacter).map(entryOf);
  });

/**
 * The open document's current definitions of the named styles: what a
 * template is about to overwrite, kept so a later template can put them back.
 * @param names - Style names (nameLocal) to capture.
 */
export const snapshotDocumentStyles = async (names: readonly string[]): Promise<StyleEntry[]> => {
  if (!names.length || !canImportTemplateStyles()) return [];
  const wanted = new Set(names.map((n) => n.toLowerCase()));
  return Word.run(async (context) => {
    const styles = context.document.getStyles();
    styles.load(STYLE_LOADS);
    await context.sync();
    return styles.items
      .filter((st) => paragraphOrCharacter(st) && wanted.has(String(st.nameLocal ?? "").toLowerCase()))
      .map(entryOf);
  });
};

/**
 * Import style entries into the open document, overwriting same-named styles.
 * @param entries - Word style JSON entries.
 * @returns The names Word reported as imported (it may report none on some hosts).
 */
export const importStyleEntries = async (entries: readonly StyleEntry[]): Promise<string[]> => {
  if (!entries.length) return [];
  return Word.run(async (context) => {
    const result = context.document.importStylesFromJson(
      JSON.stringify({ styles: entries }),
      Word.ImportedStylesConflictBehavior.overwrite
    );
    await context.sync();
    return result.value ?? [];
  });
};

/**
 * Whether the import took: the document's copy of the first paragraph style
 * that names a font now matches the template's. `null` when nothing in the
 * entries can be checked. Word's import call reported "0 styles imported" on
 * 28 Sep while the template plainly had styles; this reads the answer back
 * instead of trusting that count.
 * @param entries - The entries that were imported.
 */
export const verifyStyleImport = async (entries: readonly StyleEntry[]): Promise<boolean | null> => {
  const probe = entries.find((e) => {
    const font = e.font as { name?: string; size?: number } | null;
    return String(e.type) === "Paragraph" && font && typeof font.name === "string" && font.name;
  });
  if (!probe) return null;
  const font = probe.font as { name?: string; size?: number };
  return Word.run(async (context) => {
    const style = context.document.getStyles().getByNameOrNullObject(String(probe.nameLocal));
    style.load("isNullObject,nameLocal,font/name,font/size");
    await context.sync();
    if (style.isNullObject) return false;
    const sameName = String(style.font?.name ?? "").toLowerCase() === String(font.name).toLowerCase();
    const sameSize = typeof font.size !== "number" || Math.abs((style.font?.size ?? 0) - font.size) < 0.01;
    return sameName && sameSize;
  });
};

const FONT_KEYS = [
  "name",
  "size",
  "color",
  "bold",
  "italic",
  "underline",
  "strikeThrough",
  "subscript",
  "superscript",
] as const;
const PARAGRAPH_KEYS = [
  "alignment",
  "spaceAfter",
  "spaceBefore",
  "lineSpacing",
  "leftIndent",
  "rightIndent",
  "firstLineIndent",
  "keepWithNext",
  "keepTogether",
  "widowControl",
  "outlineLevel",
] as const;

const differs = (have: unknown, want: unknown): boolean => {
  if (typeof want === "number" && typeof have === "number") return Math.abs(have - want) > 0.01;
  if (typeof want === "string" && typeof have === "string") return have.toLowerCase() !== want.toLowerCase();
  return have !== want;
};

/**
 * Set the template's styles on the document by hand, from the same entries
 * Word's import took: whatever the read-back finds different is written
 * property by property (font, paragraph format, base and next style), and
 * a style the document lacks is added first. Word for Mac reported "137
 * styles imported" on 30 Sep and left the built-ins as they were; this is
 * the way round it. Styles Word refuses are named in `failed`.
 * @param entries - Word's style JSON, as {@link readTemplateStyleEntries} gives it.
 */
export const applyStyleEntriesByHand = async (
  entries: readonly StyleEntry[]
): Promise<{ set: string[]; added: string[]; failed: string[] }> => {
  const set: string[] = [];
  const added: string[] = [];
  const failed: string[] = [];
  const usable = entries.filter((e) => {
    const type = String(e.type);
    return e.nameLocal && (type === "Paragraph" || type === "Character");
  });
  const apply = (style: Word.Style, entry: StyleEntry, current: Record<string, unknown> | null): boolean => {
    let changed = false;
    const font = (entry.font ?? null) as Record<string, unknown> | null;
    const haveFont = (current?.font ?? null) as Record<string, unknown> | null;
    if (font) {
      for (const key of FONT_KEYS) {
        const want = font[key];
        if (want === undefined || want === null || want === "") continue;
        if (haveFont && !differs(haveFont[key], want)) continue;
        (style.font as unknown as Record<string, unknown>)[key] = want;
        changed = true;
      }
    }
    const pf = (String(entry.type) === "Paragraph" ? (entry.paragraphFormat ?? null) : null) as Record<
      string,
      unknown
    > | null;
    const havePf = (current?.paragraphFormat ?? null) as Record<string, unknown> | null;
    if (pf) {
      for (const key of PARAGRAPH_KEYS) {
        const want = pf[key];
        if (want === undefined || want === null || want === "Mixed" || want === "Unknown") continue;
        if (havePf && !differs(havePf[key], want)) continue;
        (style.paragraphFormat as unknown as Record<string, unknown>)[key] = want;
        changed = true;
      }
    }
    return changed;
  };
  const relate = (
    style: Word.Style,
    entry: StyleEntry,
    current: Record<string, unknown> | null,
    known: Set<string>
  ) => {
    for (const key of ["baseStyle", "nextParagraphStyle"] as const) {
      const want = entry[key];
      if (typeof want !== "string" || !want || !known.has(want.toLowerCase())) continue;
      if (current && !differs(current[key], want)) continue;
      (style as unknown as Record<string, unknown>)[key] = want;
    }
  };
  const loads = `isNullObject,type,baseStyle,nextParagraphStyle,font/${FONT_KEYS.join(",font/")},paragraphFormat/${PARAGRAPH_KEYS.join(",paragraphFormat/")}`;

  // One pass for everything; if Word refuses the batch, style by style so
  // one bad style does not cost the rest.
  const runAll = async (some: readonly StyleEntry[]) =>
    Word.run(async (context) => {
      const styles = context.document.getStyles();
      const found = some.map((e) => {
        const s = styles.getByNameOrNullObject(String(e.nameLocal));
        s.load(loads);
        return s;
      });
      await context.sync();
      const known = new Set(some.map((e) => String(e.nameLocal).toLowerCase()));
      const targets: { style: Word.Style; entry: StyleEntry; current: Record<string, unknown> | null }[] = [];
      some.forEach((entry, i) => {
        if (found[i].isNullObject) {
          const type = String(entry.type) === "Character" ? Word.StyleType.character : Word.StyleType.paragraph;
          const created = context.document.addStyle(String(entry.nameLocal), type);
          added.push(String(entry.nameLocal));
          targets.push({ style: created, entry, current: null });
        } else {
          targets.push({ style: found[i], entry, current: found[i].toJSON() as Record<string, unknown> });
        }
      });
      for (const t of targets) if (apply(t.style, t.entry, t.current)) set.push(String(t.entry.nameLocal));
      await context.sync();
      for (const t of targets) relate(t.style, t.entry, t.current, known);
      await context.sync();
    });
  try {
    await runAll(usable);
  } catch {
    set.length = 0;
    added.length = 0;
    for (const entry of usable) {
      try {
        await runAll([entry]);
      } catch {
        failed.push(String(entry.nameLocal));
      }
    }
  }
  return { set, added, failed };
};

/**
 * Import the styles of a .docx into the open document, overwriting styles
 * of the same name.
 * @param base64 - The template file.
 */
export const importTemplateStyles = async (base64: string): Promise<TemplateStylesResult> => {
  if (!canImportTemplateStyles()) {
    return { status: "unsupported", reason: "This Word cannot import styles (needs WordApi 1.6)." };
  }
  try {
    const entries = await readTemplateStyleEntries(base64);
    if (!entries.length) return { status: "error", message: "The template has no paragraph or character styles." };
    const imported = await importStyleEntries(entries);
    const names = entries.map((e) => String(e.nameLocal ?? "")).filter(Boolean);
    return { status: "applied", imported, count: imported.length, names };
  } catch (error) {
    return { status: "error", message: error instanceof Error ? error.message : String(error) };
  }
};
